import React, { useEffect, useState, useMemo } from "react";
import { useParams, Navigate, useSearchParams } from "react-router-dom";
import { listContent } from "@/lib/api";
import { TYPES, SEGMENT_TO_TYPE } from "@/config";
import { EntryCard } from "@/components/Cards";

export default function Archive() {
  const { segment } = useParams();
  const ctype = SEGMENT_TO_TYPE[segment];
  const [items, setItems] = useState(null);
  const [params, setParams] = useSearchParams();
  const activeCat = params.get("category") || "";

  useEffect(() => {
    if (!ctype) return;
    setItems(null);
    listContent(ctype, { limit: 200 }).then(setItems).catch(() => setItems([]));
  }, [ctype]);

  const categories = useMemo(() => {
    if (!items) return [];
    return Array.from(new Set(items.map((i) => i.category).filter(Boolean)));
  }, [items]);

  if (!ctype) return <Navigate to="/" replace />;
  const t = TYPES[ctype];

  const filtered = activeCat ? items?.filter((i) => i.category === activeCat) : items;

  return (
    <div>
      <header className="border-b border-line bg-surface">
        <div className="mx-auto max-w-[1400px] px-5 lg:px-10 py-20">
          <div className="eyebrow mb-4 text-gold">The Archive</div>
          <h1 className="font-serif text-5xl tracking-tight text-parchment lg:text-6xl">{t.label}</h1>
          <p className="mt-5 max-w-2xl font-serif text-xl leading-relaxed text-faint">{t.blurb}</p>
          <div className="eyebrow mt-6">{items ? `${items.length} entries` : "Loading…"}</div>
        </div>
      </header>

      <div className="mx-auto max-w-[1400px] px-5 lg:px-10 py-12">
        {categories.length > 0 && (
          <div className="mb-10 flex flex-wrap gap-2" data-testid="category-filters">
            <FilterChip active={!activeCat} onClick={() => setParams({})} label="All" testid="filter-all" />
            {categories.map((c) => (
              <FilterChip
                key={c}
                active={activeCat === c}
                onClick={() => setParams({ category: c })}
                label={c}
                testid={`filter-${c}`}
              />
            ))}
          </div>
        )}

        {!items && <p className="eyebrow py-16">Loading entries…</p>}
        {items && filtered.length === 0 && (
          <p className="py-16 font-serif text-2xl text-faint">No entries yet.</p>
        )}

        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4" data-testid="archive-grid">
          {filtered?.map((it) => (
            <EntryCard key={it.id} item={it} ctype={ctype} testid={`archive-card-${it.slug}`} />
          ))}
        </div>
      </div>
    </div>
  );
}

function FilterChip({ active, onClick, label, testid }) {
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      className={`border px-4 py-2 eyebrow transition-colors ${
        active ? "border-gold bg-gold/10 text-gold" : "border-line text-faint hover:border-gold/50 hover:text-parchment"
      }`}
    >
      {label}
    </button>
  );
}
